// Admin-API — Client für /api/admin/*
// Benötigt zusätzlich zum Site-Passwort (Cookie) ein Admin-Passwort
// (Header `X-Admin-Password`).

const BASE = '/api/admin';

function getHeaders(pw: string): HeadersInit {
  return {
    'X-Admin-Password': pw,
    'Content-Type': 'application/json',
  };
}

export interface BackupStatus {
  health: 'green' | 'yellow' | 'red' | 'unknown';
  gap_minutes: number;
  last_5min: string | null;
  last_5min_status: string | null;
  last_daily: string | null;
  last_daily_status: string | null;
  count_today: number;
}

export interface BackupObj {
  key: string;
  size: number;
  uploaded: string;
  type: string | null;
}

export interface BackupLogRow {
  id: number;
  type: string;
  r2_key: string;
  size_bytes: number | null;
  status: string;
  error: string | null;
  triggered_by: string;
  created_at: string;
}

export interface RestoreLogRow {
  id: number;
  source: string;
  source_key: string | null;
  pre_snapshot_id: number | null;
  status: string;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

async function handle<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error((data as unknown as { error?: string }).error ?? `${res.status}`);
  }
  return data;
}

export async function getStatus(pw: string): Promise<BackupStatus> {
  const res = await fetch(`${BASE}/backups/status`, {
    headers: getHeaders(pw),
    credentials: 'include',
  });
  return handle<BackupStatus>(res);
}

export async function listBackups(
  pw: string,
  prefix = ''
): Promise<{ objects: BackupObj[]; truncated: boolean }> {
  const q = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
  const res = await fetch(`${BASE}/backups${q}`, {
    headers: getHeaders(pw),
    credentials: 'include',
  });
  return handle<{ objects: BackupObj[]; truncated: boolean }>(res);
}

export function downloadUrl(key: string): string {
  return `${BASE}/backups/download?key=${encodeURIComponent(key)}`;
}

export async function downloadBackup(pw: string, key: string): Promise<Blob> {
  const res = await fetch(downloadUrl(key), {
    headers: { 'X-Admin-Password': pw },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Download fehlgeschlagen: ${res.status}`);
  return res.blob();
}

export async function manualBackup(pw: string) {
  const res = await fetch(`${BASE}/backups/manual`, {
    method: 'POST',
    headers: getHeaders(pw),
    credentials: 'include',
  });
  return handle<{ ok: boolean; key: string; size: number }>(res);
}

export async function restoreBackup(pw: string, key: string) {
  const res = await fetch(`${BASE}/backups/restore`, {
    method: 'POST',
    headers: getHeaders(pw),
    credentials: 'include',
    body: JSON.stringify({ key, confirm: 'RESTORE' }),
  });
  return handle<{ ok: boolean; executed: number; total: number; pre_snapshot_key: string }>(res);
}

export async function createBookmark(pw: string) {
  const res = await fetch(`${BASE}/timetravel/bookmark`, {
    headers: getHeaders(pw),
    credentials: 'include',
  });
  return handle<{ bookmark: string; created_at: string }>(res);
}

export async function timeTravelRestore(
  pw: string,
  opts: { bookmark?: string; timestamp?: string }
) {
  const res = await fetch(`${BASE}/timetravel/restore`, {
    method: 'POST',
    headers: getHeaders(pw),
    credentials: 'include',
    body: JSON.stringify({ ...opts, confirm: 'RESTORE' }),
  });
  return handle<{ ok: boolean; restored_to: string }>(res);
}

export async function getLog(pw: string) {
  const res = await fetch(`${BASE}/log`, {
    headers: getHeaders(pw),
    credentials: 'include',
  });
  return handle<{ backups: BackupLogRow[]; restores: RestoreLogRow[] }>(res);
}
